import type { SpotlightRect } from "@/interfaces/tutorial";

/**
 * Framework-free DOM plumbing for the guided tours: wait for a set of elements, and measure them.
 * Positioning lives in `utils/tour-helper.ts`.
 */

/**
 * Resolves `true` once every id exists, `false` on timeout or abort — a timeout is a supported
 * outcome, not an error. Probes cheapest first: synchronous, next frame, then a MutationObserver
 * for targets that arrive with a route change or a lazily fetched panel.
 *
 * `signal` matters: the observer scans every mutation of the document, so a closed or superseded
 * step must tear it down rather than let it idle out.
 */
export function waitForElements(
  ids: readonly string[],
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<boolean> {
  const allPresent = () => ids.every((id) => document.getElementById(id) !== null);

  if (signal?.aborted === true) return Promise.resolve(false);
  if (ids.length === 0 || allPresent()) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let observer: MutationObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let frameId: number | null = null;

    const finish = (found: boolean) => {
      if (settled) return;
      settled = true;
      if (observer !== null) observer.disconnect();
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (frameId !== null) cancelAnimationFrame(frameId);
      observer = null;
      timeoutId = null;
      frameId = null;
      signal?.removeEventListener("abort", onAbort);
      resolve(found);
    };

    function onAbort() {
      finish(false);
    }

    const check = () => {
      if (allPresent()) finish(true);
    };

    timeoutId = setTimeout(() => finish(false), timeoutMs);
    frameId = requestAnimationFrame(check);
    observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    signal?.addEventListener("abort", onAbort);
  });
}

/**
 * Scrolls the first existing id into view and reports whether anything was scrolled. `false` tells
 * the caller it is on the centered-card fallback and must not wait for a scroll that never came.
 */
export function scrollFirstIntoView(ids: readonly string[]): boolean {
  for (const id of ids) {
    const element = document.getElementById(id);
    if (element === null) continue;
    // Optional call: a detached node, or a test environment with no layout engine.
    element.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    return true;
  }
  return false;
}

/** Viewport-relative rect of each id that exists. Missing ids contribute nothing to the union. */
export function measureElements(ids: readonly string[]): SpotlightRect[] {
  return ids
    .map((id) => document.getElementById(id))
    .filter((element): element is HTMLElement => element !== null)
    .map((element) => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    });
}
